import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpStatusCode,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, Subject, switchMap, tap } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthenticatedUserService } from 'src/app/core/services/authenticated-user/authenticated-user.service';
import { AuthenticationService } from 'src/app/core/services/authentication/authentication.service';
import { SnackBarService } from 'src/app/core/services/snackbar/snackbar.service';
import { TranslationService } from 'src/app/core/services/translation/translation.service';
import { RoutesKeys } from 'src/app/utils/routes';
import errors from '../errors/errors';
import { User } from '../models/user/user';

@Injectable()
/**
 *  AuthInterceptor is an interceptor responsible to automatically add the user
 *  token in all requests that are made and to redirect the user to the login
 *  page if the token is not valid
 */
export class AuthInterceptor implements HttpInterceptor {
  refreshTokenInProgress = false;
  refreshTokenError = false;

  tokenRefreshedSource = new Subject();
  tokenRefreshed$ = this.tokenRefreshedSource.asObservable();
  automaticLogoutMessage = 'automatic-logout-message';

  /**
   *  Constructor
   * @constructor
   */
  constructor(
    private router: Router,
    private authenticatedUserService: AuthenticatedUserService,
    private authenticationService: AuthenticationService,
    private snackBarService: SnackBarService,
    private translationService: TranslationService
  ) {}

  /**
   *   Build the interceptor
   *
   *  @param {HttpRequest<any>} request request made by the user
   *  @param {HttpHandler} next next function in chain
   *  @return {Observable<HttpEvent<any>>}
   */
  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    request = this.addHeaders(request);

    return next.handle(request).pipe(
      catchError((err: any) => {
        return this.handleError(request, next, err);
      })
    );
  }

  /**
   * Handles errors that our HttpHandler catches
   * @param {HttpRequest<any>} request
   * @param {HttpHandler} next
   * @param {any} err error captured
   * @return {Observable<any>}
   */
  private handleError(
    // NOSONAR
    request: HttpRequest<any>,
    next: HttpHandler,
    err: any
  ): Observable<any> {
    if (err instanceof HttpErrorResponse) {
      if (
        err.status === HttpStatusCode.Unauthorized &&
        !request.url.endsWith('/login') &&
        !request.url.endsWith('/password/temporary/change') &&
        !request.url.endsWith('/logout') &&
        !request.url.endsWith('/refreshSession') &&
        !request.url.endsWith('/user/password/change') &&
        !request.url.endsWith('/representative/disable')
      ) {
        return this.refreshToken().pipe(
          switchMap(() => {
            request = this.addHeaders(request);
            return next.handle(request);
          }),
          catchError((refreshErr) => {
            if (this.refreshTokenError) {
              this.logout();
            } else {
              this.router.navigate([RoutesKeys.HOME]);
            }
            throw refreshErr;
          })
        );
      }
      let error: any;
      if (
        err.error &&
        err.error.errorCode &&
        ((error = errors.get(err.error.errorCode)), error !== undefined)
      ) {
        error.message = err.error?.errorMessage || '';
      } else {
        error = errors.get(`HTTP-${err.status}`);
      }
      if (error instanceof Error) {
        throw error;
      }
    }
    throw err;
  }

  /**
   * Adds Authorization and Content-Type Headers to a request
   * @param {HttpRequest<any>} request
   * @return {HttpRequest<any>}
   */
  private addHeaders(request: HttpRequest<any>): HttpRequest<any> {
    const currentUser = this.authenticatedUserService.user;

    if (this.shouldAddAuthorizationHeader(currentUser, request)) {
      const authHeader = `Bearer ${currentUser!.tokens!.accessToken}`;
      request = request.clone({
        setHeaders: {
          Authorization: authHeader,
          'Content-Type': 'application/json',
        },
      });
    } else {
      request = request.clone({
        setHeaders: {
          'Content-Type': 'application/json',
        },
      });
    }
    return request;
  }

  /**
   * Decides if authorization header should be added
   * @param {User?} user
   * @param {HttpRequest<any>} request
   * @return {boolean}
   */
  private shouldAddAuthorizationHeader(
    user: User | undefined,
    request: HttpRequest<any>
  ): boolean {
    return Boolean(
      (request.url.startsWith('/ums/api/a/') &&
        user &&
        user.tokens?.accessToken) ||
        request.url.startsWith('/rwd/api/') ||
        request.url.startsWith('/prd/api/a/') ||
        request.url.startsWith('/dta/api/a/')
    );
  }

  /**
   * Refreshes logged in user session
   * @return {Observable<any>}
   */
  private refreshToken(): Observable<any> {
    if (this.refreshTokenInProgress) {
      return new Observable((observer) => {
        this.tokenRefreshed$.subscribe({
          next: () => {
            observer.next();
            observer.complete();
          },
          error: () => {
            observer.error();
          },
        });
      });
    } else {
      this.refreshTokenInProgress = true;

      return this.authenticationService.refreshSession().pipe(
        tap(() => {
          this.refreshTokenInProgress = false;
          this.refreshTokenError = false;
          this.tokenRefreshedSource.next(undefined);
        }),
        catchError((err) => {
          this.refreshTokenInProgress = false;
          this.refreshTokenError = true;
          this.tokenRefreshedSource.error(err);
          throw err;
        })
      );
    }
  }

  /**
   * Logs out current logged in user and redirects him
   */
  private logout() {
    this.translationService
      .translateString(this.automaticLogoutMessage)
      .then((msg) => {
        this.snackBarService.warning(msg);
      });
    this.authenticationService.logout().finally(() => {
      this.router.navigate([RoutesKeys.LOGIN]);
    });
  }
}
